import { useBusinessEntity } from '@zenbusiness/application-commons-business-entity';
import {
  useButtonPressedCallback,
  useLinkPressedCallback
} from '@zenbusiness/application-commons-clickstream';
import { useMutation } from '@zenbusiness/application-commons-graphql';
import { useCaptureException } from '@zenbusiness/application-commons-logging';
import Box from '@zenbusiness/zui/Box';
import Button from '@zenbusiness/zui/Button';
import Link from '@zenbusiness/zui/Link';
import SharedAsset from '@zenbusiness/zui/SharedAsset';
import Text from '@zenbusiness/zui/Text';
import useBreakpoints from '@zenbusiness/zui/useBreakpoints';

import AddRoundedIcon from '@mui/icons-material/AddRounded';

import Skeleton from '#client/components/Skeleton';
import { BANK_OF_AMERICA_OFFER_URL } from '#client/constants/url';
import useOrderTaskContext from '#client/hooks/useOrderTaskContext';
import { REQUEST_BANKING_PRODUCT_OFFER_MUTATION } from '#client/mutations/product';
import { findLatestOrderTaskByServiceName } from '#client/utils/orderTasks';

import BankOfAmericaRequestedStatus from './BankOfAmericaRequestedStatus';

const CONTENT = {
  title: 'Bank of America Special Offer Terms and Conditions',
  linkText: 'See offer page for more details',
  buttonText: 'Yes contact me',
  offerDetails: [
    {
      title: 'Earn $300',
      description:
        'When you open a new eligible Business Advantage Banking account and deposit $5,000 or more in New Money* directly into that new eligible account within 30 days of account opening. Then, maintain an average balance of $5,000 or more during the Maintenance Period.*'
    },
    {
      title: 'Earn $500',
      description:
        'Statement credit after you apply and get approved for an eligible Bank of America® small business credit card account and make at least $5,000 in Purchases within the first 90 days of card account opening.**'
    }
  ],
  contactTerms: [
    'By selecting “Yes contact me,” you authorize Bank of America, N.A. to contact you to discuss small business products and services. You are not required to agree as a condition of purchasing any Bank of America products or services. You also consent to sharing information related to your business with Bank of America including, if available, articles of formation and your Employer Identification Number, which are required to open a new account.',
    'ZenBusiness may receive a referral fee in exchange for referring clients to Bank of America. For more information visit the ZenBusiness Privacy Policy.'
  ],
  terms: [
    '*New Money is new funds deposited into your Business Advantage Banking account that are not transfers from other Bank of America accounts or Merrill investment accounts. A transfer done via ATM, online or teller, or a transfer from a Bank of America deposit account or brokerage account such as a Merrill account is not considered new money. A cash withdrawal from an existing Bank of America account and deposited into the new Business Advantage Banking account is not considered new money. The Maintenance Period begins thirty-one (31) calendar days after account opening and ends ninety (90) calendar days after account opening.',
    'Deposit products are provided by Bank of America, N.A. Member FDIC',
    '**Requirements to Qualify for the $500 Credit Card Cash Rewards Bonus: Apply for a new eligible business credit card account (such as the Business Advantage Customized Cash Rewards credit card or the Business Advantage Unlimited Cash Rewards credit card) by December 31, 2026; AND Make at least $5,000 in Net Purchases with your card that post to your account within ninety (90) days of account opening.'
  ]
};

const BoAModalContent = () => {
  const { orderTasks, isLoadingOrderTask } = useOrderTaskContext();
  const { theme } = useBreakpoints();
  const captureException = useCaptureException();
  const { businessEntityUuid } = useBusinessEntity();
  const [requestBankingProductOffer, { loading: requestBankingProductOfferLoading }] = useMutation(
    REQUEST_BANKING_PRODUCT_OFFER_MUTATION
  );

  const seeOfferPageLinkPressed = useLinkPressedCallback({
    elementName: 'bank_of_america_offer_page_link',
    elementText: CONTENT.linkText
  });

  const bankingOfAmericaPressed = useButtonPressedCallback({
    elementName: 'bank_of_america_card',
    elementText: CONTENT.buttonText,
    elementValue: 'Opt in'
  });

  const handleButtonClick = async () => {
    bankingOfAmericaPressed();
    const { error } = await requestBankingProductOffer({ variables: { businessEntityUuid } });

    if (error) {
      captureException(error, { level: 'error', tags: { frontend: 'money-ui' } });

      throw new Error(error);
    }
  };

  const latestOfferOrder = findLatestOrderTaskByServiceName(orderTasks, 'bank_of_america_offer');

  const isAllowedToBuy = !latestOfferOrder?.type;

  const { title, linkText, buttonText, offerDetails, contactTerms, terms } = CONTENT;

  const topContent = (() => {
    if (isLoadingOrderTask) {
      return (
        <Skeleton
          data-testid="skeleton"
          height="200px"
        />
      );
    }

    if (!isAllowedToBuy) {
      return <BankOfAmericaRequestedStatus />;
    }

    // is allowed to buy
    return (
      <>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Link
            href={BANK_OF_AMERICA_OFFER_URL}
            onClick={seeOfferPageLinkPressed}
            rel="noreferrer"
            sx={{ color: theme.palette.text.blue, textDecoration: 'none' }}
            target="_blank"
          >
            {linkText}
          </Link>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'center',
            alignItems: { xs: 'center', sm: 'unset' },
            gap: '0.75rem'
          }}
        >
          <Box
            sx={{
              borderRadius: '0.75rem',
              padding: '1rem',
              backgroundColor: theme.palette.background.grey.main,
              border: `1px solid ${theme.palette.border.main}`,
              width: { xs: '100%', sm: 'calc(50% - 1.5rem)' },
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <Text
              align="center"
              color="primary"
              size="2xl"
              weight="bold"
            >
              {offerDetails[0].title}
            </Text>
            <Text
              color="primary"
              size="sm"
              sx={{ flex: 1, display: 'flex', alignItems: 'center' }}
            >
              {offerDetails[0].description}
            </Text>
          </Box>
          <AddRoundedIcon sx={{ alignSelf: { sm: 'center' } }} />
          <Box
            sx={{
              borderRadius: '0.75rem',
              padding: '1rem',
              backgroundColor: theme.palette.background.grey.main,
              border: `1px solid ${theme.palette.border.main}`,
              width: { xs: '100%', sm: 'calc(50% - 1.5rem)' },
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <Text
              align="center"
              color="primary"
              size="2xl"
              weight="bold"
            >
              {offerDetails[1].title}
            </Text>
            <Text
              color="primary"
              size="sm"
              sx={{ flex: 1, display: 'flex', alignItems: 'center' }}
            >
              {offerDetails[1].description}
            </Text>
          </Box>
        </Box>
        <Button
          loading={requestBankingProductOfferLoading}
          onClick={handleButtonClick}
          sx={{ margin: '0 auto' }}
        >
          {buttonText}
        </Button>
      </>
    );
  })();

  const bottomContent = (() => {
    if (isLoadingOrderTask) {
      return (
        <Skeleton
          data-testid="skeleton"
          height="200px"
        />
      );
    }

    if (!isAllowedToBuy) {
      return null;
    }

    // is allowed to buy
    return (
      <Box>
        {contactTerms.map((term) => (
          <Text
            key={term}
            size="xs"
          >
            {term}
          </Text>
        ))}
      </Box>
    );
  })();

  return (
    <Box
      sx={{
        width: '100%',
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
        gap: '1.5rem'
      }}
    >
      <Text
        align="center"
        color="primary"
        size="5xl"
        weight="bold"
      >
        {title}
      </Text>
      {topContent}
      <SharedAsset
        alt="Bank of America"
        height={28}
        src="/logo/Bank-of-America-Logo.svg"
        sx={{ margin: '0 auto 1.5rem auto' }}
        width={280}
      />
      {bottomContent}
      {terms.map((term) => (
        <Text
          key={term}
          size="xs"
        >
          {term}
        </Text>
      ))}
    </Box>
  );
};

export default BoAModalContent;
